import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Check, VideoOff } from 'lucide-react'
import {
  QuizModeProvider,
  QuizRenderer,
  QuizResultToggle,
  QuizStyleProvider,
  extractSolutionVideoUrl,
  type QuizSolveResult,
} from '@quebon/shared-learning'
import {
  checkReviewVideo,
  getSpecialLectureReview,
  isUnitAssessmentNotFound,
  type UnitAssessmentReviewItem,
  type UnitAssessmentReviewView,
} from '@/api/unitAssessment'
import {
  SolutionVideoPlayer,
  type SolutionVideoPlayerHandle,
} from '@/components/special-lecture/SolutionVideoPlayer'
import { ReviewCompletionDialog } from '@/components/special-lecture/ReviewCompletionDialog'
import { Button } from '@/components/learner/ui/Button'
import { SpecialLectureLoading, SpecialLecturePageState } from './SpecialLecturePageState'

/** item.answer(JSON 배열 문자열 또는 평문) → string[]. */
function parseAnswers(raw: string | null): string[] {
  if (!raw) return []
  const trimmed = raw.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) return parsed.map((v) => String(v))
    } catch {
      /* fallthrough */
    }
  }
  return [trimmed]
}

function getCorrectAnswers(item: UnitAssessmentReviewItem): string[] {
  if (Array.isArray(item.correctAnswer) && item.correctAnswer.length > 0) {
    return item.correctAnswer.map((value) => String(value))
  }
  const snapshot = item.questionSnapshot
  if (!snapshot) return []
  if (Array.isArray(snapshot.correctAnswers) && snapshot.correctAnswers.length > 0) {
    return snapshot.correctAnswers.map((value) => String(value))
  }
  if (Array.isArray(snapshot.answers) && snapshot.answers.length > 0) {
    return snapshot.answers
      .filter((answer) => typeof answer === 'object' && answer != null && 'correct' in answer && answer.correct)
      .map((answer) => String(answer.answer))
  }
  return []
}

function ReviewItemCard({
  item,
  index,
  onVideoEnded,
}: {
  item: UnitAssessmentReviewItem
  index: number
  onVideoEnded: (quizId: string) => void
}) {
  const { t } = useTranslation('learner')
  const videoUrl = extractSolutionVideoUrl(item.explanation)
  const correct = item.isCorrect === true
  const videoRef = useRef<SolutionVideoPlayerHandle | null>(null)
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(true)
  const studentAnswers = parseAnswers(item.answer)
  const itemLabel = t('specialLecture.review.itemNo', { no: index + 1 })
  const solveResult: QuizSolveResult = {
    correct,
    corrects: item.corrects?.length ? item.corrects : studentAnswers.map(() => correct),
    correctAnswers: getCorrectAnswers(item),
  }

  return (
    <section className="rounded-2xl bg-lc-v3-gray-100 p-2">
      <div className="flex flex-col gap-6 rounded-2xl border border-lc-v3-gray-300 bg-white px-4 pb-6 pt-5 sm:px-8 sm:pb-8 sm:pt-6">
        {item.questionSnapshot ? (
          <div className="relative" onClickCapture={() => videoRef.current?.pause()}>
            <QuizStyleProvider
              variant="lc"
              headerSuffix={(
                <span className="text-lc-v3-body-1 font-semibold text-lc-text-tertiary">
                  {itemLabel}
                </span>
              )}
            >
              <QuizRenderer
                quizType={(item.questionSnapshot.type as string) ?? 'multi_choice'}
                quizId={item.quizId}
                quiz={item.questionSnapshot}
                mode="student"
                initialAnswers={studentAnswers}
                solveResult={solveResult}
                isViewSolved
                showReallyTrue={showCorrectAnswer}
                hideSubmitButton
                hideExplanation
              />
            </QuizStyleProvider>
            <span
              className={`absolute right-0 top-0 inline-flex min-w-14 items-center justify-center rounded-full px-3 py-1.5 text-lc-v3-caption-1 font-semibold text-white ${
                correct ? 'bg-lc-success-500' : 'bg-lc-error-500'
              }`}
            >
              {correct ? t('specialLecture.review.correct') : t('specialLecture.review.wrong')}
            </span>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <span className="text-lc-v3-body-1 font-semibold text-lc-text-tertiary">
              {itemLabel}
            </span>
            <span
              className={`inline-flex min-w-14 items-center justify-center rounded-full px-3 py-1.5 text-lc-v3-caption-1 font-semibold text-white ${
                correct ? 'bg-lc-success-500' : 'bg-lc-error-500'
              }`}
            >
              {correct ? t('specialLecture.review.correct') : t('specialLecture.review.wrong')}
            </span>
          </div>
        )}

        <QuizResultToggle
          value={showCorrectAnswer ? 'correct-and-explanation' : 'my-answer'}
          onChange={(next) => {
            const shouldShowCorrectAnswer = next === 'correct-and-explanation'
            if (!shouldShowCorrectAnswer) videoRef.current?.pause()
            setShowCorrectAnswer(shouldShowCorrectAnswer)
          }}
        />

        {showCorrectAnswer ? (
          <div className="rounded-2xl border border-lc-success-100 bg-lc-success-50 p-4 sm:p-6">
            {videoUrl ? (
              <div className="flex flex-col gap-6">
                <div className="px-2">
                  <h2 className="text-lc-v3-h4 font-bold text-lc-text-primary">
                    {t('specialLecture.review.solutionVideo')}
                  </h2>
                </div>
                <SolutionVideoPlayer
                  ref={videoRef}
                  videoUrl={videoUrl}
                  title={t('specialLecture.review.solutionVideo')}
                  onEnded={() => onVideoEnded(item.quizId)}
                  playLabel={t('specialLecture.review.play')}
                  pauseLabel={t('specialLecture.review.pause')}
                  progressLabel={t('specialLecture.review.videoProgress')}
                  rewindLabel={t('specialLecture.review.rewind')}
                  seekPolicyLabel={t('specialLecture.review.seekPolicy')}
                  restrictSeeking
                  watchStatus={{
                    watched: item.videoWatched,
                    watchedLabel: t('specialLecture.review.videoWatched'),
                    autoLabel: t('specialLecture.review.videoAutoComplete'),
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-lc-v3-gray-300 bg-white/70 px-5 py-8 text-center">
                <span className="inline-flex size-12 items-center justify-center rounded-full bg-lc-v3-gray-200 text-lc-text-tertiary">
                  <VideoOff className="size-6" strokeWidth={1.8} aria-hidden />
                </span>
                <div className="flex flex-col gap-1">
                  <h2 className="text-lc-v3-body-1 font-bold text-lc-text-secondary">
                    {t('specialLecture.review.noVideoTitle')}
                  </h2>
                  <p className="text-lc-v3-body-2 text-lc-text-tertiary">
                    {t('specialLecture.review.noVideo')}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </section>
  )
}

function ReviewNavigation({
  items,
  currentIndex,
  onSelect,
}: {
  items: UnitAssessmentReviewItem[]
  currentIndex: number
  onSelect: (index: number) => void
}) {
  const { t } = useTranslation('learner')

  return (
    <nav aria-label={t('specialLecture.review.navigation')}>
      <div className="overflow-x-auto pb-2">
        <div className="flex w-max min-w-full items-start gap-6">
          <div className="flex shrink-0 flex-col gap-4 text-lc-v3-body-1 font-semibold text-lc-text-secondary">
            <span className="flex h-10 w-[74px] items-center">
              {t('specialLecture.review.navigationProblem')}
            </span>
            <span className="flex h-6 w-[74px] items-center whitespace-nowrap">
              {t('specialLecture.review.navigationVideo')}
            </span>
          </div>
          <div className="flex gap-3" role="tablist">
            {items.map((item, index) => {
              const isCurrent = index === currentIndex
              const hasVideo = extractSolutionVideoUrl(item.explanation) != null
              const videoStatus = !hasVideo
                ? t('specialLecture.review.noVideoShort')
                : item.videoWatched
                  ? t('specialLecture.review.watchedShort')
                  : t('specialLecture.review.unwatchedShort')
              const resultStatus = item.isCorrect === true
                ? 'correct'
                : item.isCorrect === false
                  ? 'wrong'
                  : 'pending'
              const resultLabel = resultStatus === 'correct'
                ? t('specialLecture.review.correct')
                : resultStatus === 'wrong'
                  ? t('specialLecture.review.wrong')
                  : ''
              const resultClass = resultStatus === 'correct'
                ? isCurrent
                  ? 'border-lc-success-500 bg-lc-success-500 text-white shadow-lc-v3-active'
                  : 'border-lc-success-100 bg-lc-success-50 text-lc-text-success'
                : resultStatus === 'wrong'
                  ? isCurrent
                    ? 'border-lc-error-500 bg-lc-error-500 text-white shadow-lc-v3-active'
                    : 'border-lc-error-100 bg-lc-error-50 text-lc-text-error'
                  : isCurrent
                    ? 'border-lc-key-500 bg-lc-key-500 text-white shadow-lc-v3-active'
                    : 'border-lc-v3-gray-300 bg-white text-lc-text-secondary'

              return (
                <div key={item.quizId} className="flex w-10 shrink-0 flex-col items-center gap-4">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={isCurrent}
                    aria-controls="special-lecture-review-panel"
                    aria-label={t('specialLecture.review.navigationItem', {
                      no: index + 1,
                      status: resultLabel ? `${resultLabel}, ${videoStatus}` : videoStatus,
                    })}
                    data-result={resultStatus}
                    onClick={() => onSelect(index)}
                    className={`inline-flex size-10 shrink-0 items-center justify-center rounded-[10px] border text-[15px] font-semibold transition focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500 ${resultClass}`}
                  >
                    {index + 1}
                  </button>
                  <span className="flex size-6 items-center justify-center" aria-hidden>
                    {hasVideo ? (
                      <span className={`inline-flex size-5 items-center justify-center rounded-full ${
                        item.videoWatched ? 'bg-lc-key-500 text-white' : 'bg-lc-v3-gray-300 text-white'
                      }`}>
                        <Check className="size-3.5 stroke-[3]" />
                      </span>
                    ) : null}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-t border-lc-v3-gray-300 pt-4 text-[13px] font-medium text-[#374151]">
        <span className="inline-flex items-center gap-2">
          <span className="size-3 rounded-full bg-lc-success-500" aria-hidden />
          {t('specialLecture.review.legendCorrect')}
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="size-3 rounded-full bg-lc-error-500" aria-hidden />
          {t('specialLecture.review.legendWrong')}
        </span>
        <span className="inline-flex items-center gap-2">
          <span className="inline-flex size-4 items-center justify-center rounded-full bg-lc-key-500 text-white" aria-hidden>
            <Check className="size-3 stroke-[3]" />
          </span>
          {t('specialLecture.review.legendWatched')}
        </span>
      </div>
    </nav>
  )
}

/** 단원평가 특강 리뷰 — 문제+내답+정오답+손풀이영상+문항별 자동 시청 체크. */
export default function SpecialLectureReview() {
  const { id = '' } = useParams<{ id: string }>()
  const numericId = Number(id)
  const navigate = useNavigate()
  const { t } = useTranslation('learner')
  const [review, setReview] = useState<UnitAssessmentReviewView | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showCompletionDialog, setShowCompletionDialog] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      setReview(await getSpecialLectureReview(numericId))
    } catch (e) {
      if (isUnitAssessmentNotFound(e)) {
        navigate('/my', { replace: true })
        return
      }
      setError(t('specialLecture.error.load'))
    }
  }, [navigate, numericId, t])

  useEffect(() => {
    load()
  }, [load])

  const onVideoEnded = useCallback(
    (quizId: string) => {
      setReview((prev) =>
        prev
          ? { ...prev, items: prev.items.map((it) => (it.quizId === quizId ? { ...it, videoWatched: true } : it)) }
          : prev,
      )
      void checkReviewVideo(numericId, quizId, true).catch(() => load())
    },
    [numericId, load],
  )

  if (error) {
    return (
      <SpecialLecturePageState
        kind="error"
        title={t('specialLecture.state.errorTitle')}
        description={error}
        actionLabel={t('specialLecture.state.retry')}
        onAction={() => {
          setReview(null)
          void load()
        }}
      />
    )
  }
  if (!review) {
    return <SpecialLectureLoading />
  }

  if (review.items.length === 0) {
    return (
      <SpecialLecturePageState
        kind="empty"
        title={t('specialLecture.state.emptyTitle')}
        description={t('specialLecture.state.emptyDescription')}
        actionLabel={t('specialLecture.state.retry')}
        onAction={() => {
          setReview(null)
          void load()
        }}
      />
    )
  }

  const currentItem = review.items[currentIndex] ?? review.items[0]
  const videoItems = review.items.filter((item) => extractSolutionVideoUrl(item.explanation) != null)
  const watchedCount = videoItems.filter((item) => item.videoWatched).length
  const isReviewComplete = videoItems.length === 0 || watchedCount === videoItems.length
  const isFirstItem = currentIndex === 0
  const isLastItem = currentIndex === review.items.length - 1
  const hasBottomAction = !isFirstItem || !isLastItem || isReviewComplete
  const selectItem = (index: number) => {
    setCurrentIndex(index)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <QuizModeProvider value={{ mode: 'student' }}>
      <QuizStyleProvider variant="lc">
        <main className="min-h-screen bg-lc-bg-secondary px-4 py-6 sm:py-10">
          <div className="mx-auto flex w-full max-w-[960px] flex-col gap-10">
            <div className="flex flex-col gap-5 rounded-[20px] bg-white px-3 pb-3 pt-2 shadow-lc-v3-card sm:gap-6 sm:px-4 sm:pb-4">
              <header className="flex flex-col gap-7 p-3 sm:gap-10 sm:p-4">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex flex-col gap-2">
                    <p className="text-lc-v3-body-2 font-bold text-lc-text-link">
                      {t('specialLecture.review.progress', { current: currentIndex + 1, total: review.items.length })}
                    </p>
                    <h1 className="text-lc-v3-h3 font-bold text-lc-text-primary">
                      {t('specialLecture.review.title')}
                    </h1>
                    <p className="max-w-2xl text-lc-v3-body-1 text-lc-text-secondary">
                      {t('specialLecture.review.guide')}
                    </p>
                  </div>
                  <div className={`grid w-full shrink-0 gap-3 sm:flex sm:w-auto sm:items-center ${
                    review.score != null ? 'grid-cols-2' : 'grid-cols-1'
                  }`}>
                    {review.score != null ? (
                      <div className="flex min-h-20 flex-col items-center justify-center gap-1.5 rounded-2xl bg-lc-success-50 px-3 text-center sm:size-[88px]">
                        <p className="text-[11px] font-semibold tracking-[0.5px] text-lc-text-success">
                          {t('specialLecture.review.scoreLabel')}
                        </p>
                        <p className="text-[20px] font-bold text-lc-text-primary">
                          {t('specialLecture.review.score', { score: review.score })}
                        </p>
                      </div>
                    ) : null}
                    <div className="flex min-h-20 flex-col items-center justify-center gap-1.5 rounded-2xl bg-lc-key-50 px-3 text-center sm:size-[88px]">
                      <p className="text-[11px] font-semibold tracking-[0.5px] text-lc-key-500">
                        {t('specialLecture.review.watchedLabel')}
                      </p>
                      <p className="text-[20px] font-bold text-lc-text-primary">
                        {videoItems.length > 0
                          ? `${watchedCount}/${videoItems.length}`
                          : t('specialLecture.review.watchedEmpty')}
                      </p>
                    </div>
                  </div>
                </div>
                <ReviewNavigation items={review.items} currentIndex={currentIndex} onSelect={selectItem} />
              </header>

              <div id="special-lecture-review-panel" role="tabpanel" className="outline-none">
                <ReviewItemCard
                  key={currentItem.quizId}
                  item={currentItem}
                  index={currentIndex}
                  onVideoEnded={onVideoEnded}
                />
              </div>
            </div>

            {hasBottomAction ? (
              <div className="flex items-center justify-center gap-5 px-1 pb-6">
                {!isFirstItem ? (
                  <Button
                    variant="neutral"
                    size="XL"
                    className="w-[120px] border-lc-v3-gray-300 bg-lc-v3-gray-50 text-lc-text-secondary hover:bg-lc-v3-gray-100"
                    onClick={() => selectItem(currentIndex - 1)}
                  >
                    {t('specialLecture.review.previous')}
                  </Button>
                ) : null}
                {!isLastItem ? (
                  <Button
                    variant="navy"
                    size="XL"
                    className="w-[120px]"
                    onClick={() => selectItem(currentIndex + 1)}
                  >
                    {t('specialLecture.review.next')}
                  </Button>
                ) : isReviewComplete ? (
                  <Button
                    variant="navy"
                    size="XL"
                    className="w-[120px]"
                    onClick={() => setShowCompletionDialog(true)}
                  >
                    {t('specialLecture.review.complete')}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
          <ReviewCompletionDialog open={showCompletionDialog} onConfirm={() => setShowCompletionDialog(false)} />
        </main>
      </QuizStyleProvider>
    </QuizModeProvider>
  )
}
